import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAuth } from '../../../../containers/UseAuth';
import useCurrencySpace from '../../../../hooks/budget/useCurrencySpaces';
import {
    formatNumber,
    getCpmMax,
    isNumber,
    numberWithoutCommas,
} from '../../../../utils/functions';
import Hint from '../../../Tools/Hint';
import { RequiredTag } from '../../../Input/RequiredTag';
import { useEffect, useMemo } from 'react';

const Budget = ({
    budget,
    validator,
    totalSpent,
    currencies,
    budgets,
    updateBasics,
    currencyDefault,
    selectedScreens,
    selectedCurrency,
}) => {
    const { t } = useTranslation();
    const auth = useAuth();
    const currencySpace = useCurrencySpace(selectedCurrency?.symbol);
    const noMoneyPlaceholder = '0.00';

    const change = (attribute, value) => {
        const newBudget = Object.assign({}, budget);
        if (
            auth.session?.user?.cpm_mode === 0 &&
            attribute === 'cpmOptimizer' &&
            value
        ) {
            newBudget.cpm = getCpmMax(selectedScreens);
        }
        newBudget[attribute] = value;
        updateBasics('budget', newBudget);
    };

    const handleChangeBudget = (amount) => {
        if (isNumber(amount)) {
            amount = numberWithoutCommas(amount);
            amount = amount === '0' ? '' : amount;
            change('budget', amount);
        } else {
            change('budget', 0);
        }
    };

    const handleChangeCurrency = (currencyId) => {
        const [selectedCurrency] = currenciesToUse.filter(
            (c) => c.id == currencyId
        );
        updateBasics('selectedCurrency', selectedCurrency);
    };

    const availableCurrencies = useMemo(() => {
        if (!budgets) {
            return currencies.map((c) => c.short_name);
        }
        return Object.keys(budgets);
    }, [budgets, currencies]);

    const currenciesToUse = useMemo(() => {
        return currencies.filter((item) =>
            availableCurrencies.includes(item.short_name)
        );
    }, [currencies, availableCurrencies]);

    useEffect(() => {
        // Initialize selectedCurrency with proper priority:
        // 1. Use currencyDefault if it's among currenciesToUse.
        // 2. Otherwise, fall back to the first available currency.
        if (selectedCurrency) {
            return;
        }
        const hasDefaultInList =
            currencyDefault &&
            currenciesToUse.some((c) => c.id === currencyDefault.id);
        if (hasDefaultInList) {
            updateBasics('selectedCurrency', currencyDefault);
        } else if (currenciesToUse.length > 0) {
            updateBasics('selectedCurrency', currenciesToUse[0]);
        }
    }, [currenciesToUse, currencyDefault, selectedCurrency, updateBasics]);

    return (
        <div className={'module rounded m-1 mb-4'}>
            <div className="header flex justify-between content-center">
                <div className="title text-xl font-extralight">
                    {t(
                        'sections.campaigns.new-campaign.wizard-basics.budget.title'
                    )}
                </div>
                <div className="brief font-light text-xs text-gray-400 mt-1.5 text-right">
                    {budget.budget === '' || budget.budget === 0 ? (
                        t(
                            'sections.campaigns.new-campaign.wizard-basics.budget.info'
                        )
                    ) : (
                        <span className="text-xs">
                            {selectedCurrency?.symbol}
                            {formatNumber(budget.budget, 'currency')}
                        </span>
                    )}
                </div>
            </div>
            <div className="body rounded-sm mt-2">
                <div className="flex justify-between flex-col md:flex-row">
                    <div className="lg:w-3/6 2xl:w-2/6">
                        <label
                            htmlFor="budget"
                            className="block text-sm font-medium text-gray-700 mb-2">
                            {t(
                                'sections.campaigns.new-campaign.wizard-basics.budget.budget-label'
                            )}{' '}
                            <RequiredTag alert={validator.errors?.budget} />
                        </label>
                        <div
                            className={`relative shadow-sm w-full sm:text-sm rounded-md mt-1 outline-none transition-colors ${
                                validator.errors.budget
                                    ? 'border-red-500 ring-1 ring-red-500 hover:border-red-500'
                                    : 'border-gray-300 focus:outline-none focus:ring-0 focus:outline-none hover:border-gray-400'
                            }`}>
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <span className="text-gray-500 sm:text-sm">
                                    {selectedCurrency &&
                                        selectedCurrency?.symbol}
                                </span>
                            </div>
                            <input
                                type="text"
                                name="budget"
                                id="budget"
                                value={
                                    !parseInt(budget.budget) === 0
                                        ? ''
                                        : formatNumber(
                                              budget.budget,
                                              'withcommas'
                                          )
                                }
                                min="0"
                                autoComplete="off"
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    const positiveValue = newValue.replace(
                                        /^-/,
                                        ''
                                    );
                                    handleChangeBudget(positiveValue);
                                }}
                                className={`focus:ring-0 focus:outline-none block w-full sm:text-sm rounded-md border-gray-300 ${currencySpace}`}
                                placeholder={noMoneyPlaceholder}
                                data-testid="budget"
                                onBlur={() => {
                                    validator.touchField('budget');
                                    validator.validateField('budget');
                                }}
                            />

                            <div className="absolute inset-y-0 right-0 flex items-center">
                                <label htmlFor="currency" className="sr-only">
                                    {t('common.currency.currency-title')}
                                </label>
                                <select
                                    id="currency"
                                    name="currency"
                                    value={
                                        selectedCurrency
                                            ? selectedCurrency.id
                                            : ''
                                    }
                                    onChange={(e) =>
                                        handleChangeCurrency(e.target.value)
                                    }
                                    className="focus:ring-0 focus:outline-none h-full py-0 pl-2 pr-7 border-none bg-transparent text-gray-500 sm:text-sm rounded-md">
                                    {currenciesToUse.map((currency, index) => (
                                        <option
                                            value={currency.id}
                                            disabled={totalSpent > 0}
                                            key={index}>
                                            {currency.short_name}
                                        </option>
                                    ))}
                                </select>
                                {totalSpent > 0 && (
                                    <Hint
                                        className="absolute -right-5"
                                        title={t(
                                            'sections.campaigns.new-campaign.wizard-screens.screens.currency'
                                        )}
                                        text={t(
                                            'sections.campaigns.new-campaign.wizard-basics.budget.hasSpent'
                                        )}
                                        type={'info'}
                                    />
                                )}
                            </div>
                        </div>
                        {validator.shouldShowError('budget') && (
                            <p className="mt-1 text-sm text-red-500">
                                {validator.errors.budget}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Budget;
